import { useEffect, useState } from "react";
import { X, Sparkles } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import avatarLisa from "@/assets/avatar-lisa.png";
import avatarSam from "@/assets/avatar-sam.png";
import avatarNova from "@/assets/avatar-nova.png";

interface Tip {
  avatar: string;
  from: string;
  text: string;
  cta?: { label: string; to: string };
}

const TIPS: Tip[] = [
  {
    avatar: avatarLisa,
    from: "Lisa",
    text: "Meer kandidaten nodig? Met een Boost promoten we je vacature op Facebook en Instagram, precies bij de juiste mensen.",
    cta: { label: "Boost vacature", to: "/bedrijf/vacature-boost" },
  },
  {
    avatar: avatarSam,
    from: "Sam",
    text: "Check regelmatig de TalentRadar voor nieuwe matches — er komen dagelijks kandidaten bij.",
    cta: { label: "Open TalentRadar", to: "/kandidaten" },
  },
  {
    avatar: avatarNova,
    from: "Nova",
    text: "Geen CV? Maak gewoon telefonisch kennis. Een belletje zegt vaak meer dan papier.",
    cta: { label: "Naar kandidaten", to: "/bedrijf/matches" },
  },
  {
    avatar: avatarSam,
    from: "Sam",
    text: "Laat een AI-recruiter voor je zoeken terwijl jij verder werkt.",
    cta: { label: "AI Recruiters", to: "/bedrijf/ai-recruiter" },
  },
  {
    avatar: avatarLisa,
    from: "Lisa",
    text: "Reageer binnen 24 uur op een match — dat verdubbelt de kans op een gesprek.",
    cta: { label: "Bekijk matches", to: "/bedrijf/matches" },
  },
];

const LAST_TIP_KEY = "company-tip-bubble-last";

const readIdx = (): number => {
  try {
    const v = Number(sessionStorage.getItem(LAST_TIP_KEY));
    return Number.isInteger(v) && v >= 0 && v < TIPS.length ? v : -1;
  } catch {
    return -1;
  }
};

/** Kies een nieuwe tip (nooit dezelfde als vorige keer) en onthoud die. */
const pickTip = (): Tip => {
  const last = readIdx();
  const options = TIPS.map((_, i) => i).filter((i) => i !== last);
  const idx = options[Math.floor(Math.random() * options.length)] ?? 0;
  try {
    sessionStorage.setItem(LAST_TIP_KEY, String(idx));
  } catch {
    /* ignore */
  }
  return TIPS[idx];
};

const CONTROL_ROOM = "/bedrijf/control-room";
const MOBILE_HIDDEN = ["/profiel/", "/vacatures/nieuw", "/vacatures/bewerken", "/bedrijf/matches", "/bedrijf/ai-recruiter/"];

export const CompanyTipBubble = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [tip, setTip] = useState<Tip | null>(() => {
    if (pathname === CONTROL_ROOM) return null;
    const idx = readIdx();
    return idx >= 0 ? TIPS[idx] : null;
  });
  const [open, setOpen] = useState(false);

  const isMobile = useIsMobile();

  useEffect(() => {
    if (pathname !== CONTROL_ROOM) return;
    setTip(pickTip());
    // On phones the tip never opens by itself (it would cover the list).
    if (typeof window !== "undefined" && window.innerWidth < 768) return;
    const t = setTimeout(() => setOpen(true), 4000);
    return () => clearTimeout(t);
  }, [pathname]);

  // Keep the new-campaign page calm: no tip bubble over its action bar.
  // Boost pages have their own navigation bar in the bottom-right corner
  // The chat has its send button in the bottom-right corner, so no bubble there either.
  if (!tip || pathname.startsWith("/bedrijf/vacature-boost") || pathname.startsWith("/bedrijf/berichten")) return null;
  // Phones: pages with a fixed action bar at the bottom get no bubble.
  if (isMobile && MOBILE_HIDDEN.some((p) => pathname.startsWith(p))) return null;

  return (
    <div className="fixed bottom-4 right-4 md:bottom-6 md:right-6 z-40 flex flex-col items-end gap-2 pointer-events-none max-md:bottom-[calc(5rem+env(safe-area-inset-bottom))] max-md:right-3">
      {open && (
        <div
          className={cn(
            "pointer-events-auto relative w-[min(19rem,calc(100vw-2rem))] rounded-2xl bg-white/95 backdrop-blur",
            "border border-cyan/20 p-4 pr-9",
            "shadow-[0_18px_45px_-18px_hsl(var(--cyan-dark)/0.45)]",
            "animate-in fade-in slide-in-from-bottom-2 duration-300",
          )}
        >
          <button
            type="button"
            aria-label="Tip sluiten"
            onClick={() => setOpen(false)}
            className="absolute top-2 right-2 max-md:top-0 max-md:right-0 w-7 h-7 max-md:w-11 max-md:h-11 rounded-full flex items-center justify-center text-foreground/40 hover:text-foreground hover:bg-foreground/5 transition-colors"
          >
            <X className="w-4 h-4" strokeWidth={2.5} />
          </button>

          <div className="flex items-center gap-1.5 mb-1.5">
            <Sparkles className="w-3.5 h-3.5 text-cyan-dark" />
            <span className="text-[10px] max-md:text-xs font-black uppercase tracking-wider text-cyan-dark">
              Tip van {tip.from}
            </span>
          </div>

          <p className="text-sm text-foreground/80 leading-relaxed">{tip.text}</p>

          <div className="mt-3 flex items-center gap-2 flex-wrap">
            {tip.cta && (
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  navigate(tip.cta!.to);
                }}
                className="inline-flex items-center justify-center rounded-xl bg-yellow text-yellow-foreground px-3.5 h-9 text-xs font-black transition-transform hover:-translate-y-0.5"
              >
                {tip.cta.label}
              </button>
            )}
          </div>
        </div>
      )}

      <button
        type="button"
        aria-label={open ? "Tip verbergen" : "Tip bekijken"}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "pointer-events-auto relative w-14 h-14 max-md:w-11 max-md:h-11 rounded-full overflow-hidden bg-cyan-light",
          "ring-2 ring-white shadow-[0_10px_28px_-8px_hsl(var(--cyan-dark)/0.5)]",
          "transition-transform duration-300 hover:scale-105 active:scale-95",
        )}
      >
        <img
          src={tip.avatar}
          alt={`AI recruiter ${tip.from}`}
          loading="lazy"
          className="w-full h-full object-cover"
        />
        {!open && (
          <span className="absolute bottom-0.5 right-0.5 w-3.5 h-3.5 rounded-full bg-yellow border-2 border-white animate-pulse" />
        )}
      </button>
    </div>
  );
};
